import { Component, ElementRef, OnDestroy, ViewChild, HostListener } from "@angular/core";
import { ResizeObserverScheduler } from "shared/helper-services/resize-observer-scheduler";
import { GoldenLayout } from "./golden-layout";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";

import { LoggerService } from "@bfx/logger";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";
import { Subscription } from "rxjs";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "golden-layout-navigator",
  templateUrl: "./golden-layout-navigator.html",
  host: {
    "(document:click)": "onClick($event)",
  },
  standalone: false,
})
export class GoldenLayoutNavigator implements OnDestroy {
  public showDropdown: boolean = false;

  public visible: boolean = false;

  @ViewChild("dropdown", { static: true }) dropdownMenu: ElementRef;

  private window: NativeDesktopWindow = null;

  private lastShowTime: number = 0;

  /** Map of header elements and the resizeObserver that is listening to size changes on the header */
  private resizeObservers: Map<Element, ResizeObserverScheduler> = new Map();

  private subscriptions: Subscription[] = [];

  constructor(
    public element: ElementRef,
    public goldenLayout: GoldenLayout,
    public goldenLayoutService: GoldenLayoutService,
  ) {
    this.subscriptions.push(this.goldenLayout.refreshSubject.subscribe(this.refresh.bind(this)));
    this.subscriptions.push(
      this.goldenLayout.dragStartSubject.subscribe(this.hideDropdown.bind(this)),
    );
    this.subscriptions.push(
      this.goldenLayoutService.titleUpdateChange$.subscribe(this.onTabTitleChange.bind(this)),
    );
  }

  public ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.

    for (const observer of this.resizeObservers.values()) {
      // Ensure all resize listeners are removed
      observer.complete();
    }
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());

    if (this.window) {
      this.window.close();
    }
  }

  public refresh(): void {
    let element: Element = this.element.nativeElement;

    let headers: HTMLCollectionOf<Element> =
      element.ownerDocument.body.getElementsByClassName("lm_header");

    const unprocessedElements: Set<Element> = new Set(this.resizeObservers.keys());

    for (let i: number = 0; i < headers.length; i++) {
      if (
        !headers[i].classList.contains("tab-overflow-menu") &&
        !headers[i].parentElement?.classList.contains("lm_dragProxy")
      ) {
        // we haven't seen this header before so we need to add the overflow and listen
        // for size changes
        this.refreshHeader(headers[i]);
        unprocessedElements.delete(headers[i]);
      }
    }
    if (unprocessedElements.size > 0) {
      // Remaining set of elements that no longer exist so we must clean up
      logger.out.info(`gln-c - disposing of ${unprocessedElements.size} resize listeners`);
      for (const element of unprocessedElements) {
        const resizeObserver = this.resizeObservers.get(element);
        if (resizeObserver) {
          resizeObserver.complete();
          this.resizeObservers.delete(element);
        }
      }
    }
  }

  protected refreshHeader(element: Element): void {
    let dropDowns: HTMLCollectionOf<Element> = element.getElementsByClassName("lm_dropdown");

    if (dropDowns.length == 0) {
      let dropDown: Element = document.createElement("button");
      dropDown.setAttribute(
        "class",
        "lm_dropdown icon_ion_menu_vert bfx-icon-button-secondary-bold-sm",
      );
      dropDown.setAttribute("style", "display: none");
      dropDown.setAttribute("title", "More open tabs...");
      dropDown["onclick"] = this.toggleDropdown.bind(this, dropDown);
      element.appendChild(dropDown);

      // Create a new obbserver for size changes that will call onResize()
      const resizeObserver = new ResizeObserverScheduler((entries) => {
        entries.forEach((entry) => this.onResize(entry.target));
      }, this.constructor.name);
      resizeObserver.observe(element);
      this.resizeObservers.set(element, resizeObserver);
    }
    setTimeout(this.onResize.bind(this, element), 300);
  }

  public toggleDropdown(element: any, event: PointerEvent) {
    event.stopPropagation();

    let header: HTMLDivElement = element.parentElement;
    let maxWidth: number = Math.ceil(header.getBoundingClientRect().width - 60);
    let tabs: HTMLCollectionOf<Element> = header.getElementsByClassName("lm_tab");
    let sum: number = 0;

    let dropDown: Element = this.dropdownMenu.nativeElement;
    let holder: Element = dropDown.firstElementChild;

    if (!holder.classList.contains("bfx-filter-bar-options")) {
      logger.out.warn(
        `${this.constructor.name}.toggleDropdown() preventing removeChild() on incorrect holder`,
      );
    }

    while (holder.classList.contains("bfx-filter-bar-options") && holder.firstElementChild) {
      holder.removeChild(holder.firstElementChild);
    }

    for (let i: number = 0; i < tabs.length; i++) {
      sum += Math.ceil(tabs[i].getBoundingClientRect().width);

      if (logger.out.isDebugEnabled())
        logger.out.debug(
          `${this.constructor.name}.toggleDropdown ` +
            `sum: ${sum} maxWidth: ${maxWidth} tabs.length: ${tabs.length}`,
        );

      if (sum > maxWidth) {
        let clone = tabs[i].cloneNode(true);
        clone["style"].marginLeft = "unset";
        clone["style"].visibility = "unset";
        clone["classList"].remove("lm_active");
        clone["onclick"] = this.actionSelected.bind(this, tabs[i]);
        holder.appendChild(clone);
      }
    }

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.showDropdown = false;
    } else {
      this.showDropdown = !this.showDropdown;
    }

    this.dropdownMenu.nativeElement.style.width = "200px";

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.handleNativeDesktopEvent(this.dropdownMenu.nativeElement, element);
      return;
    }

    const bounds = element.getBoundingClientRect();
    this.dropdownMenu.nativeElement.style.left = bounds.right - 200 + "px";
    this.dropdownMenu.nativeElement.style.top = bounds.bottom + "px";
  }

  protected handleNativeDesktopEvent(element: any, sourceElement: any) {
    let openWindowPromise = Promise.resolve();

    if (this.window == null) {
      let windowName: string = "layout-navigator-dropdown-" + Math.random();
      openWindowPromise = new Promise<void>((res) => {
        NativeDesktopSupport.openWindow(
          windowName,
          WindowType.Utility,
          false,
          null,
          ((wnd: NativeDesktopWindow) => {
            this.window = wnd;
            element.style.top = "0px";
            element.style.right = "0px";
            element.style.width = "100%";
            element.style.height = "100%";
            element.style.position = "unset";
            element.style.overflow = "hidden";
            this.window.getNativeWindow().document.body.style.overflow = "hidden";
            this.window.getNativeWindow().document.body.appendChild(element);
            this.window["beforeClose"] = (() => {
              if (new Date().getTime() - this.lastShowTime > 100) {
                this.showDropdown = false;
                this.window.hide();
              }
              return false;
            }).bind(this);
            res();
          }).bind(this),
        );
      });
    } else {
      const body = this.window.getNativeWindow().document.body;
      if (body.firstElementChild !== element) {
        logger.out.info(
          `${this.constructor.name}.handleNativeDesktopEvent() reattaching element to body`,
        );
        this.window.getNativeWindow().document.body.appendChild(element);
      }
    }

    openWindowPromise.then(() => {
      let left: number =
        sourceElement.offsetParent.offsetLeft + sourceElement.offsetParent.offsetWidth - 200;
      let top: number =
        sourceElement.offsetParent.offsetTop + sourceElement.offsetParent.offsetHeight;

      left *= NativeDesktopSupport.zoomLevel;
      top *= NativeDesktopSupport.zoomLevel;

      let width = 200;
      let height = this.dropdownMenu.nativeElement.firstElementChild.children.length * 32 + 6;

      width *= NativeDesktopSupport.zoomLevel;
      height *= NativeDesktopSupport.zoomLevel;

      let window: Window = NativeDesktopSupport.getNativeDesktopWindow().getNativeWindow();
      left += window.screenLeft;
      top += window.screenTop + 44;

      if (logger.out.isDebugEnabled())
        logger.out.debug(
          `${this.constructor.name}.handleNativeDesktopEvent setBounds` +
            `(left: ${left}, top: ${top}, width: ${width}), height: ${height}`,
        );

      this.window.setBounds(left, top, width, height).then(() => {
        this.lastShowTime = new Date().getTime();
        this.window.show().then(() => {
          logger.out.info(`${this.constructor.name}.handleNativeDesktopEvent() show window`);
        });
        this.showDropdown = true;
      });
    });
  }

  public hideDropdown(): void {
    if (this.window != null) {
      this.window.hide();
    } else {
      this.showDropdown = false;
    }
  }

  public actionSelected(element: Element, event): void {
    if (event.srcElement.className == "lm_close_tab" || event.srcElement.className == "lm_popout") {
      let target: any = element.getElementsByClassName(event.srcElement.className)[0];
      target.click();
    } else {
      this.goldenLayout.activateItem(element);
    }

    this.hideDropdown();
  }

  protected onResize(element: Element): void {
    let sum: number = 0;
    let dropdown: Element = element.getElementsByClassName("lm_dropdown")[0];
    let tabs: HTMLCollectionOf<Element> = element.getElementsByClassName("lm_tab");
    // 34 PX here is width of button + 2 gaps - would
    let width: number = Math.max(Math.ceil(element.getBoundingClientRect().width) - 34, 0);
    for (let i: number = 0; i < tabs.length; i++) {
      sum += Math.ceil(tabs[i].getBoundingClientRect().width);
      tabs[i]["style"].visibility = sum > width ? "hidden" : "unset";

      if (logger.out.isDebugEnabled())
        logger.out.debug(
          `${this.constructor.name}.refreshResize() ` + `i: ${i} sum: ${sum} width: ${width} `,
        );

      if (i > 0 && sum > width && tabs[i].classList.contains("lm_active")) {
        if (logger.out.isDebugEnabled())
          logger.out.debug(`${this.constructor.name}.refreshResize() ` + `i ${i} activateItem `);

        this.goldenLayout.activateItem(tabs[i]);
      }
    }

    dropdown["style"].display = sum > width ? "" : "none";
  }

  protected onResizeHeader(element: Element): void {
    let sum: number = 0;
    let tabs: HTMLCollectionOf<Element> = element.getElementsByClassName("lm_tab");

    // 34 PX here is width of button + 2 gaps - would
    let width: number = Math.max(Math.ceil(element.getBoundingClientRect().width) - 34, 0);

    for (let i: number = 0; i < tabs.length; i++) {
      sum += Math.ceil(tabs[i].getBoundingClientRect().width);
      tabs[i]["style"].visibility = sum > width ? "hidden" : "unset";
    }
  }

  /**
   * Handle a change in the text of a tab.
   * @param _blotterId
   */
  private onTabTitleChange(_blotterId: string) {
    // For now let's just refresh every tab header
    logger.out.info("gln-c - refreshing all tab headers after title change");
    this.refresh();
  }

  /**
   * Works on desktop
   */
  @HostListener("window:resize", ["$event"])
  public onClick(event): void {
    this.hideDropdown();
  }
}
